import React from "react";
import { Container, Skeleton, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import styles from "./pendingLotsTableStyles.js";
import LotsTableActions from "../LotsTableActions/index.js";

const PendingLotsTable = ({ lots = [], isLoading, fetchLots, search, setHasItemsSelected, setSelectedLots, selectedLots = [] }) => {

  const columns = [
    {
      field: "id",
      headerName: "NO. LOTE",
      width: 90,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>NO. LOTE</strong>,
    },
    {
      field: "user",
      headerName: "NOMBRE",
      minWidth: 200,
      flex: 1,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>NOMBRE</strong>,
    },
    {
      field: "area",
      headerName: "AREA",
      type: "string",
      minWidth: 90,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>AREA</strong>,
    },
    {
      field: "zone",
      headerName: "ZONA",
      type: "string",
      minWidth: 90,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ZONA</strong>,
    },
    {
      field: "sector",
      headerName: "SECTOR",
      type: "string",
      minWidth: 90,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>SECTOR</strong>,
    },
    {
      field: "subsector",
      headerName: "SUB",
      type: "string",
      minWidth: 50,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>SUB</strong>,
    },
    {
      field: "total_guides",
      headerName: "GUIAS",
      type: "string",
      width: 70,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>GUIAS</strong>,
    },
    {
      field: "total_weight",
      headerName: "PESO",
      type: "string",
      width: 60,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>PESO</strong>,
    },
    {
      field: "actions",
      headerName: "ACCIONES",
      type: "string",
      width: 100,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ACCIONES</strong>,
      renderCell: (params) => <LotsTableActions fetchLots={fetchLots} lot={params.row} />,
    },
  ];

  const filteredRows = lots.filter((guide) => {
    return Object.values(guide).some((value) =>
      value.toString().toLowerCase().includes(search.toLowerCase())
    );
  });

  const rows = filteredRows?.map((lot, index) => ({
    id: lot.id_batch,
    user: lot.user,
    zone: lot.zone ?? 'Sin zona',
    area: lot.area ?? 'Sin area',
    sector: lot.sector ?? 'Sin sector',
    subsector: lot.subsector ?? 'Sin subsector',
    postal_code: lot.postal_code,
    total_guides: lot.total_guides,
    total_weight: lot.total_weight,
    latitude: lot.latitude,
    longitude: lot.longitude,
    id_location: lot.id_location,
  }));

  const handleSelectionChange = (newSelection) => {
    // Combinar las nuevas selecciones con las selecciones previas que no están visibles
    const currentVisibleIds = rows.map(row => row.id);
    const previousNonVisibleSelections = selectedLots.filter(id => 
      !currentVisibleIds.includes(id)
    );
    
    const combinedSelections = [...new Set([...previousNonVisibleSelections, ...newSelection])];
    
    setSelectedLots(combinedSelections);
    setHasItemsSelected(combinedSelections.length > 0);
  };

  // Solo las selecciones que son visibles en las filas actuales
  const visibleSelections = selectedLots.filter(id => 
    rows.some(row => row.id === id)
  );

  return (
    <Container sx={styles.mainContainer} style={styles.principalContainer}>
      {isLoading ? (
        <Container sx={styles.skeletonMainContainer}>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'5%'} height={40} />
            <Skeleton variant="rounded" width={'5%'} height={40} />
            <Skeleton variant="rounded" width={'20%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'15%'} height={40} />
          </Container>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'5%'} height={300} />
            <Skeleton variant="rounded" width={'5%'} height={300} />
            <Skeleton variant="rounded" width={'20%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'15%'} height={300} />
          </Container>
        </Container>
      ) : (
        <DataGrid
          autoHeight
          disableColumnFilter
          rows={rows}
          columns={columns}
          rowSelectionModel={visibleSelections}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          getRowHeight={() => 'auto'} 
          sx={{
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { 
              py: '8px', 
              display: 'flex', 
              alignItems: 'center' 
            },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { 
              py: '6px', 
              display: 'flex', 
              alignItems: 'center' 
            },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { 
              py: '22px', 
              display: 'flex', 
              alignItems: 'center' 
            },
          }}          
          checkboxSelection
          pageSizeOptions={[10, 20, 50, 100]}
          disableColumnMenu={true}
          disableSelectionOnClick={true}
          onRowSelectionModelChange={handleSelectionChange}
          isRowSelectable={(params) =>
            params.row.latitude !== 0 && params.row.longitude !== 0
          }
          localeText={{
            noRowsLabel: (
              <NoResultsComponent message={"No se encontrarón lotes"} />
            ),
          }}
        />
      )}
    </Container>
  );
};

const NoResultsComponent = ({ message }) => (
  <Container sx={styles.noDataContainer}>
    <SearchOffIcon sx={styles.noDataIcon} />
    <Typography sx={styles.noDataTitle}>{message}</Typography>
  </Container>
);

export default PendingLotsTable;
